import { forwardRef } from 'react';
import MuiButton from '@mui/material/Button';
import MuiIconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

/** This application's button vocabulary, mapped onto MUI's variant/colour pair. */
const VARIANTS = {
  primary: { variant: 'contained', color: 'primary' },
  secondary: { variant: 'outlined', color: 'inherit' },
  outline: { variant: 'outlined', color: 'primary' },
  ghost: { variant: 'text', color: 'primary' },
  danger: { variant: 'contained', color: 'error' }
};

/**
 * The only button in the application.
 *
 * `type` defaults to "button". HTML's default is "submit", which means a button
 * dropped into a form submits it unless somebody remembered otherwise — a bug
 * that only appears once there is a form around it. Submitting is opted into
 * here, not out of.
 *
 * While `loading` is set the button is disabled and keeps its label, so the
 * control neither resizes nor loses the word the user was reading. The spinner
 * is decoration; the button carries aria-busy, which is what gets announced.
 */
export const Button = forwardRef(function Button(
  {
    type = 'button',
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading = false,
    disabled = false,
    leadingIcon,
    children,
    ...rest
  },
  ref
) {
  const mapped = VARIANTS[variant] ?? VARIANTS.primary;
  const muiSize = size === 'sm' ? 'small' : size === 'lg' ? 'large' : 'medium';

  return (
    <MuiButton
      ref={ref}
      type={type}
      size={muiSize}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      startIcon={loading ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : leadingIcon}
      {...mapped}
      {...rest}
    >
      {children}
    </MuiButton>
  );
});

/**
 * A button that reads as plain text, for a secondary action beside a real one.
 * Still a button — anything that acts rather than navigates must be one, or it
 * is unreachable by keyboard.
 */
export const TextButton = forwardRef(function TextButton(props, ref) {
  return <Button ref={ref} variant="ghost" {...props} />;
});

/**
 * An icon-only button.
 *
 * `label` is required and becomes the accessible name — an icon button without
 * one is announced as just "button", which tells a screen-reader user nothing.
 */
export const IconButton = forwardRef(function IconButton({ label, size = 'md', children, ...rest }, ref) {
  return (
    <MuiIconButton
      ref={ref}
      aria-label={label}
      title={label}
      size={size === 'sm' ? 'small' : size === 'lg' ? 'large' : 'medium'}
      {...rest}
    >
      {children}
    </MuiIconButton>
  );
});
